import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useCheckPhoneExistsMutation,
  useLoginMutation,
} from "api/endpoints/auth";
import { useAppDispatch } from "store/store-hooks";
import { setToken } from "store/reducers/auth/auth.reducer";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { ROUTE_PATH } from "router/router-constant/router.constant";

type AuthStep = "phone" | "password";

interface PhoneFormValues {
  phoneNumber: string;
}

interface PasswordFormValues {
  password: string;
}

const phoneValidationSchema = Yup.object({
  phoneNumber: Yup.string()
    .matches(/^09\d{9}$/, "شماره تلفن باید با 09 شروع شده و 11 رقم باشد")
    .required("شماره تلفن الزامی است"),
});

const passwordValidationSchema = Yup.object({
  password: Yup.string()
    .required("رمز عبور الزامی است"),
});

export const AuthLoginNewComponent = () => {
  const [currentStep, setCurrentStep] = useState<AuthStep>("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [, setUserId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [checkPhoneExists, { isLoading: isCheckingPhone }] =
    useCheckPhoneExistsMutation();
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handlePhoneSubmit = async (values: PhoneFormValues) => {
    try {
      setErrorMessage("");
      const result = await checkPhoneExists({
        payload: { phoneNumber: values.phoneNumber },
      }).unwrap();

      setPhoneNumber(values.phoneNumber);

      if (result.exists && result.userId) {
        setUserId(result.userId);
        setCurrentStep("password");
      } else {
        setErrorMessage(
          "شماره تلفن شما ثبت نشده است. برای ثبت نام باید از طریق لینک دعوت وارد شوید."
        );
      }
    } catch (err: any) {
      setErrorMessage(err?.data?.message || "خطایی در بررسی شماره تلفن رخ داد");
    }
  };

  const handlePasswordSubmit = async (values: PasswordFormValues) => {
    try {
      setErrorMessage("");
      const result = await login({
        payload: {
          phoneNumber,
          password: values.password,
        },
      }).unwrap();

      // Store the token and user info
      dispatch(
        setToken({
          token: result.token,
          userId: result.user.id,
          user: result.user,
        })
      );

      // Redirect to main page and let RoleBasedRedirect handle the routing
      navigate("/");
    } catch (err: any) {
      const error = err as AxiosError<{ stack: string }>;
      const errMessage = error.response?.data?.stack;
      setErrorMessage(errMessage || "رمز عبور اشتباه است");
    }
  };

  const getStepIndex = () => {
    switch (currentStep) {
      case "phone":
        return 0;
      case "password":
        return 1;
      default:
        return 0;
    }
  };

  const steps = ["شماره تلفن", "رمز عبور"];

  const renderPhoneForm = () => (
    <Formik
      initialValues={{ phoneNumber: "" }}
      validationSchema={phoneValidationSchema}
      onSubmit={handlePhoneSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form>
          <TextField
            fullWidth
            name="phoneNumber"
            label="شماره تلفن"
            value={values.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.phoneNumber && Boolean(errors.phoneNumber)}
            helperText={touched.phoneNumber && errors.phoneNumber}
            margin="normal"
            placeholder="09123456789"
            dir="ltr"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isCheckingPhone}
            sx={{ 
              mt: 3, 
              mb: 2,
              minHeight: { xs: "48px", sm: "36px" },
              fontSize: { xs: "1rem", sm: "0.875rem" }
            }}
          >
            {isCheckingPhone ? "در حال بررسی..." : "ادامه"}
          </Button>
        </Form>
      )}
    </Formik>
  );

  const renderPasswordForm = () => (
    <Formik
      initialValues={{ password: "" }}
      validationSchema={passwordValidationSchema}
      onSubmit={handlePasswordSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form>
          <Typography variant="body2" sx={{ mb: 2 }}>
            شماره تلفن: {phoneNumber}
          </Typography>

          <TextField
            fullWidth
            name="password"
            label="رمز عبور (4 رقم آخر شماره تلفن)"
            type="password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.password && Boolean(errors.password)}
            helperText={
              touched.password && errors.password
                ? errors.password
                : "4 رقم آخر شماره تلفن خود را وارد کنید"
            }
            margin="normal"
            dir="ltr"
          />

          <Box sx={{ 
            display: "flex", 
            gap: 2, 
            mt: 3, 
            mb: 2,
            flexDirection: { xs: "column", sm: "row" }
          }}>
            <Button
              onClick={() => setCurrentStep("phone")}
              variant="outlined"
              fullWidth
              sx={{ 
                order: { xs: 2, sm: 1 },
                minHeight: { xs: "48px", sm: "36px" }
              }}
            >
              بازگشت
            </Button>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoggingIn}
              sx={{ 
                order: { xs: 1, sm: 2 },
                minHeight: { xs: "48px", sm: "36px" },
                fontSize: { xs: "1rem", sm: "0.875rem" }
              }}
            >
              {isLoggingIn ? "در حال ورود..." : "ورود"}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="grey.100"
      p={{ xs: 1, sm: 2 }}
    >
      <Paper elevation={3} sx={{ 
        p: { xs: 3, sm: 4 }, 
        maxWidth: 500, 
        width: "100%",
        borderRadius: { xs: 2, sm: 3 }
      }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}>
            ورود به Velora
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.9rem", sm: "0.875rem" } }}>
            به پنل مدیریت اشتراک خود خوش آمدید
          </Typography>
        </Box>

        <Stepper activeStep={getStepIndex()} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {currentStep === "phone" && renderPhoneForm()}
        {currentStep === "password" && renderPasswordForm()}
      </Paper>
    </Box>
  );
};
